import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatEUR, formatDate } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { useWaves } from "../context/WaveContext";
import { Plus, Trash2, Edit, Upload, Download, ChevronDown, ChevronRight, Check, List, CalendarDays, ChevronLeft, ChevronsLeft, ChevronsRight } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const empty = { restaurant_id: "", date: "", meal_type: "dinner", sessions: 1, status: "confirmed", dietas: 1, assigned_employee_ids: [], notes: "" };

export default function Events() {
  const { user } = useAuth();
  const { currentWave } = useWaves();
  const isAdmin = ["admin", "editor", "operator"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState(empty);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCity, setFilterCity] = useState("all");
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [collapsedRest, setCollapsedRest] = useState({});
  const [viewMode, setViewMode] = useState("calendar"); // "list" | "calendar"
  const [calMonth, setCalMonth] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() }; // month: 0-11
  });
  // Sync calendar month to currentWave.month_from when wave changes (first load)
  const lastWaveIdRef = useRef(null);
  useEffect(() => {
    if (!currentWave?.id) return;
    if (lastWaveIdRef.current === currentWave.id) return;
    lastWaveIdRef.current = currentWave.id;
    const mf = currentWave?.month_from;
    if (mf && /^\d{4}-\d{2}$/.test(mf)) {
      const [y, m] = mf.split("-").map(Number);
      setCalMonth({ year: y, month: m - 1 });
    }
  }, [currentWave?.id, currentWave?.month_from]);
  // Quick-create popover state {restaurantId, date}
  const [quickCreate, setQuickCreate] = useState(null);
  const [quickForm, setQuickForm] = useState({ status: "confirmed", eventos: 1, dietas: 1 });
  // Assign-employees dialog state {event}
  const [assignDialog, setAssignDialog] = useState(null);
  // Event picker dialog when a cell has multiple events
  const [eventPicker, setEventPicker] = useState(null); // {events: [...]}

  const load = async () => {
    const rs = await api.get("/restaurants").then(({ data }) => data);
    setRestaurants(rs);
    const es = await api.get("/events").then(({ data }) => data);
    setItems(es);
    // Best-effort load employees (admin-only resource). Failure is fine.
    try {
      const { data } = await api.get("/employees");
      setEmployees(data.employees || []);
    } catch { setEmployees([]); }
  };
  useEffect(() => { load(); }, []);

  // Open quick-create popover on empty cell
  const openQuickCreate = (restaurantId, date) => {
    setQuickForm({ status: "confirmed", eventos: 1, dietas: 1 });
    setQuickCreate({ restaurantId, date });
  };

  // Submit quick-create: creates 1 or 2 events with same status.
  // Dietas value in the form represents the TOTAL for the day; it is split
  // evenly across the created events (dinner takes the remainder when odd),
  // so `dietas=2` + `eventos=2` results in 1+1 (not 2+2).
  const submitQuickCreate = async () => {
    if (!quickCreate) return;
    const { restaurantId, date } = quickCreate;
    const { status, eventos, dietas } = quickForm;
    const services = eventos === 2 ? ["lunch", "dinner"] : ["dinner"];
    const dietasPer = eventos === 2
      ? { lunch: Math.floor(dietas / 2), dinner: Math.ceil(dietas / 2) }
      : { dinner: dietas };
    try {
      for (const meal of services) {
        await api.post("/events", {
          restaurant_id: restaurantId,
          date,
          meal_type: meal,
          sessions: 1,
          status,
          dietas: dietasPer[meal] ?? 0,
          assigned_employee_ids: [],
          notes: "",
        });
      }
      toast.success(eventos === 2 ? "2 eventos creados" : "Evento creado");
      setQuickCreate(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  // Assign employees to an existing event
  const submitAssign = async (eventId, employeeIds) => {
    const ev = items.find((e) => e.id === eventId);
    if (!ev) return;
    if (ev.status === "provisional") {
      return toast.error("No se pueden asignar empleados a un evento provisional. Confírmalo primero.");
    }
    try {
      await api.put(`/events/${eventId}`, {
        restaurant_id: ev.restaurant_id,
        date: ev.date,
        meal_type: ev.meal_type,
        sessions: ev.sessions || 1,
        status: ev.status,
        dietas: ev.dietas ?? 1,
        assigned_employee_ids: employeeIds,
        notes: ev.notes || "",
      });
      toast.success("Empleados actualizados");
      setAssignDialog(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const save = async () => {
    if (!form.restaurant_id || !form.date) return toast.error("Restaurante y fecha son obligatorios");
    const payload = { ...form, sessions: Number(form.sessions) || 1 };
    try {
      if (editingId) await api.put(`/events/${editingId}`, payload);
      else await api.post("/events", payload);
      toast.success("Guardado");
      setOpen(false); setForm(empty); setEditingId(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const startEdit = (e) => { setForm({ ...e }); setEditingId(e.id); setOpen(true); };
  const remove = async (e) => {
    if (!window.confirm("¿Eliminar evento?")) return;
    await api.delete(`/events/${e.id}`); toast.success("Eliminado"); load();
  };

  const confirmEvent = async (e) => {
    const payload = { ...e, status: "confirmed_late" };
    delete payload.id; delete payload.created_at; delete payload.wave_id;
    await api.put(`/events/${e.id}`, payload);
    toast.success("Evento confirmado");
    load();
  };

  const revertToProvisional = async (e) => {
    const assignedCount = (e.assigned_employee_ids || []).length;
    const msg = assignedCount > 0
      ? `¿Devolver a Provisional? Se quitarán ${assignedCount} empleado(s) asignado(s).`
      : "¿Devolver este evento a Provisional (naranja)?";
    if (!window.confirm(msg)) return;
    const payload = { ...e, status: "provisional", assigned_employee_ids: [] };
    delete payload.id; delete payload.created_at; delete payload.wave_id;
    await api.put(`/events/${e.id}`, payload);
    toast.success("Evento vuelto a provisional");
    load();
  };

  const importSubmit = async () => {
    if (!importFile) return toast.error("Selecciona un archivo");
    setImportLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const { data } = await api.post(`/import/excel`, fd, {
        params: { wipe: false },
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Importado: +${data.created_events} eventos · ${data.skipped_duplicates} duplicados omitidos`);
      setImportOpen(false); setImportFile(null); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error en la importación");
    } finally { setImportLoading(false); }
  };

  const restMap = useMemo(() => Object.fromEntries(restaurants.map(r => [r.id, r])), [restaurants]);
  const filtered = items.filter((i) => {
    const r = restMap[i.restaurant_id];
    return (filterStatus === "all" || i.status === filterStatus) &&
           (filterCity === "all" || (r?.city || "Sin ciudad") === filterCity);
  });

  // Turnos EFECTIVOS por evento = restaurant.sessions_per_day / nº comidas ese día (misma fórmula
  // que facturación/cashout/asistencia). MAX con el legacy event.sessions para preservar data histórica.
  // Siempre se consulta sobre `items` (todos los eventos de la wave) para que los filtros de la UI
  // no alteren el cálculo del denominador (p.ej. filtrar por ciudad no debe cambiar el nº de turnos).
  const effSessions = (e) => {
    const r = restMap[e.restaurant_id];
    const spd = Number(r?.sessions_per_day) || 1;
    const mealsThatDay = new Set(
      items
        .filter((ev) => ev.restaurant_id === e.restaurant_id && ev.date === e.date)
        .map((ev) => ev.meal_type)
    ).size || 1;
    const eff = Math.max(1, Math.round(spd / mealsThatDay));
    return Math.max(eff, Number(e.sessions) || 1);
  };

  // Group by restaurant
  const grouped = restaurants
    .filter(r => filterCity === "all" || (r.city || "Sin ciudad") === filterCity)
    .map((r) => {
      const evts = filtered.filter((e) => e.restaurant_id === r.id).sort((a, b) => a.date.localeCompare(b.date));
      let totalSessions = 0, totalPax = 0, totalV90 = 0, eventCount = 0;
      evts.forEach((e) => {
        const s = effSessions(e);
        totalSessions += s;
        // Event count rule: 1-2 turnos = 1 evento, 3-4 turnos = 2 eventos
        eventCount += Math.max(1, Math.ceil(s / 2));
        const pax = (r.pax_per_session || 0) * s;
        totalPax += pax;
        totalV90 += pax * 0.9 * (r.price_per_pax || 0);
      });
      return { restaurant: r, events: evts, totalSessions, totalPax, totalV90, eventCount };
    })
    .filter((g) => g.events.length > 0);

  // Global summary across the (filtered) groups
  const summary = useMemo(() => {
    let restaurants = grouped.length;
    let events = 0, sessions = 0, paxMax = 0, paxMin = 0, value90 = 0, value100 = 0;
    let confirmed = 0, late = 0, provisional = 0;
    grouped.forEach((g) => {
      const r = g.restaurant;
      const pps = Number(r.pax_per_session || 0);
      const pmin = r.pax_min_per_session
        ? Number(r.pax_min_per_session)
        : pps * (r.billing_base_percent || 90) / 100;
      const price = Number(r.price_per_pax || 0);
      g.events.forEach((e) => {
        const s = effSessions(e);
        events += Math.max(1, Math.ceil(s / 2));
        sessions += s;
        paxMax += pps * s;
        paxMin += pmin * s;
        value100 += pps * s * price;
        value90 += pmin * s * price;
        if (e.status === "confirmed") confirmed += 1;
        else if (e.status === "confirmed_late") late += 1;
        else if (e.status === "provisional") provisional += 1;
      });
    });
    return { restaurants, events, sessions, paxMax, paxMin, value90, value100, confirmed, late, provisional };
  }, [grouped]);
  const fmtN = (n) => {
    const v = Number(n || 0);
    return Number.isInteger(v) ? v.toString() : v.toLocaleString("es-ES", { maximumFractionDigits: 2 });
  };

  const cities = useMemo(() => Array.from(new Set(restaurants.map((r) => r.city || "Sin ciudad"))).sort(), [restaurants]);
  const toggleRest = (rid) => setCollapsedRest({ ...collapsedRest, [rid]: !collapsedRest[rid] });

  return (
    <div className="p-6 md:p-10 max-w-7xl" data-testid="events-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="label-eyebrow">Agenda · {currentWave?.name}</p>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight mt-2">Eventos</h1>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <Button
              onClick={async () => {
                try {
                  const preview = await api.post("/events/normalize-dietas", null, { params: { dry_run: true } });
                  const n = preview.data.would_update || 0;
                  if (n === 0) {
                    toast.info("No hay eventos duplicados para corregir");
                    return;
                  }
                  if (!window.confirm(`Se detectaron ${n} eventos con dietas>1 en días con comida+cena. ¿Reducirlos a 1 dieta cada uno?`)) return;
                  const res = await api.post("/events/normalize-dietas");
                  toast.success(`${res.data.updated} eventos corregidos`);
                  load();
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Error");
                }
              }}
              variant="outline"
              className="rounded-none border-amber-400 text-amber-800 hover:bg-amber-50"
              data-testid="normalize-dietas-button"
              title="Escanea la wave y corrige eventos con dietas duplicadas en días con comida+cena"
            >
              Corregir dietas
            </Button>
            <Button onClick={() => setImportOpen(true)} variant="outline" className="rounded-none border-zinc-300" data-testid="upload-excel-button">
              <Upload className="h-4 w-4 mr-2" strokeWidth={1.5} /> Subir Excel
            </Button>
            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(empty); setEditingId(null); } }}>
              <DialogTrigger asChild>
                <Button data-testid="add-event-button" className="rounded-none bg-zinc-900 hover:bg-zinc-800">
                  <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} /> Nuevo evento
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-none">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl">{editingId ? "Editar" : "Nuevo"} evento</DialogTitle>
                  <DialogDescription>El aforo y precio se calculan desde el restaurante.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3 py-2">
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Restaurante</Label>
                    <Select value={form.restaurant_id} onValueChange={(v) => setForm({ ...form, restaurant_id: v })}>
                      <SelectTrigger className="rounded-none" data-testid="event-restaurant"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                      <SelectContent className="rounded-none">{restaurants.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs uppercase tracking-wider">Fecha</Label>
                      <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-none" data-testid="event-date" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider">Servicio</Label>
                      <Select value={form.meal_type} onValueChange={(v) => setForm({ ...form, meal_type: v })}>
                        <SelectTrigger className="rounded-none" data-testid="event-meal"><SelectValue /></SelectTrigger>
                        <SelectContent className="rounded-none">
                          <SelectItem value="lunch">Comida</SelectItem>
                          <SelectItem value="dinner">Cena</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider">Turnos</Label>
                      <Input type="number" min="1" value={form.sessions} onChange={(e) => setForm({ ...form, sessions: e.target.value })} className="rounded-none" data-testid="event-sessions" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Estado</Label>
                    <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                      <SelectTrigger className="rounded-none" data-testid="event-status"><SelectValue /></SelectTrigger>
                      <SelectContent className="rounded-none">
                        <SelectItem value="confirmed">Confirmado (verde)</SelectItem>
                        <SelectItem value="confirmed_late">Nuevo confirmado</SelectItem>
                        <SelectItem value="provisional">Provisional (naranja)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  {editingId && (
                    <Button
                      data-testid="delete-event-button"
                      onClick={async () => {
                        if (!window.confirm("¿Eliminar evento?")) return;
                        await api.delete(`/events/${editingId}`);
                        toast.success("Eliminado");
                        setOpen(false); setForm(empty); setEditingId(null);
                        load();
                      }}
                      variant="outline"
                      className="rounded-none border-red-400 text-red-700 hover:bg-red-50 mr-auto"
                    >
                      <Trash2 className="h-4 w-4 mr-2" strokeWidth={1.5} /> Eliminar
                    </Button>
                  )}
                  <Button data-testid="save-event-button" onClick={save} className="rounded-none bg-zinc-900 hover:bg-zinc-800">Guardar</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6" data-testid="events-summary">
        <div className="bg-zinc-900 text-white p-4">
          <p className="label-eyebrow text-zinc-400">Restaurantes</p>
          <p className="font-heading font-mono text-2xl mt-1">{summary.restaurants}</p>
        </div>
        <div className="bg-white border border-zinc-200 p-4">
          <p className="label-eyebrow">Eventos</p>
          <p className="font-heading font-mono text-2xl mt-1">{summary.events}</p>
          <p className="text-[10px] text-zinc-500 mt-0.5">{summary.confirmed} conf · {summary.late} nuevos · {summary.provisional} prov</p>
        </div>
        <div className="bg-white border border-zinc-200 p-4">
          <p className="label-eyebrow">Turnos</p>
          <p className="font-heading font-mono text-2xl mt-1">{summary.sessions}</p>
        </div>
        <div className="bg-white border border-zinc-200 p-4">
          <p className="label-eyebrow">Aforo mín · máx</p>
          <p className="font-heading font-mono text-2xl mt-1">{fmtN(summary.paxMin)} · <span className="text-zinc-500 text-lg">{fmtN(summary.paxMax)}</span></p>
        </div>
        <div className="bg-white border border-zinc-200 p-4">
          <p className="label-eyebrow">Valor base (90%)</p>
          <p className="font-heading font-mono text-2xl mt-1">{formatEUR(summary.value90)}</p>
        </div>
        <div className="bg-white border border-zinc-200 p-4">
          <p className="label-eyebrow">Valor 100% aforo</p>
          <p className="font-heading font-mono text-2xl mt-1">{formatEUR(summary.value100)}</p>
        </div>
      </div>

      <div className="mb-6 flex gap-3 max-w-4xl items-end">
        <div className="flex-1">
          <Label className="text-xs uppercase tracking-wider">Ciudad</Label>
          <Select value={filterCity} onValueChange={setFilterCity}>
            <SelectTrigger className="rounded-none" data-testid="filter-city"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="all">Todas</SelectItem>
              {cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1">
          <Label className="text-xs uppercase tracking-wider">Estado</Label>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="rounded-none" data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="confirmed">Confirmados</SelectItem>
              <SelectItem value="confirmed_late">Nuevos confirmados</SelectItem>
              <SelectItem value="provisional">Provisionales</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex border border-zinc-300 bg-white" data-testid="view-toggle">
          <button
            onClick={() => setViewMode("list")}
            data-testid="view-list"
            className={`px-4 py-2 text-xs uppercase tracking-wider flex items-center gap-2 transition-colors ${viewMode === "list" ? "bg-zinc-900 text-white" : "text-zinc-700 hover:bg-zinc-50"}`}
          >
            <List className="h-3.5 w-3.5" strokeWidth={1.5} /> Lista
          </button>
          <button
            onClick={() => setViewMode("calendar")}
            data-testid="view-calendar"
            className={`px-4 py-2 text-xs uppercase tracking-wider flex items-center gap-2 transition-colors ${viewMode === "calendar" ? "bg-zinc-900 text-white" : "text-zinc-700 hover:bg-zinc-50"}`}
          >
            <CalendarDays className="h-3.5 w-3.5" strokeWidth={1.5} /> Calendario
          </button>
        </div>
      </div>

      {viewMode === "calendar" && (
        <CalendarView
          calMonth={calMonth}
          setCalMonth={setCalMonth}
          events={filtered}
          restaurants={restaurants}
          restMap={restMap}
          filterCity={filterCity}
          onEdit={isAdmin ? startEdit : null}
          onCreate={isAdmin ? openQuickCreate : null}
          onAssign={isAdmin ? (ev) => setAssignDialog({ event: ev }) : null}
          onPickEvent={isAdmin ? (evs) => setEventPicker({ events: evs }) : null}
          employees={employees}
          waveMonthFrom={currentWave?.month_from || null}
          waveMonthTo={currentWave?.month_to || null}
        />
      )}

      {viewMode === "list" && (
      <div className="space-y-4" data-testid="events-grouped">
        {grouped.length === 0 && <div className="bg-white border border-zinc-200 p-10 text-center text-sm text-zinc-500">Sin eventos.</div>}
        {grouped.map((g) => {
          const collapsed = collapsedRest[g.restaurant.id];
          return (
            <div key={g.restaurant.id} className="bg-white border border-zinc-200" data-testid={`group-${g.restaurant.id}`}>
              <button onClick={() => toggleRest(g.restaurant.id)} className="w-full px-6 py-4 border-b border-zinc-200 bg-zinc-50 hover:bg-zinc-100 flex items-center justify-between text-left transition-colors">
                <div className="flex items-center gap-3">
                  {collapsed ? <ChevronRight className="h-4 w-4 text-zinc-500" strokeWidth={1.5} /> : <ChevronDown className="h-4 w-4 text-zinc-500" strokeWidth={1.5} />}
                  <div>
                    <p className="label-eyebrow">{g.restaurant.city}</p>
                    <h3 className="font-heading text-xl mt-0.5">{g.restaurant.name}</h3>
                  </div>
                </div>
                <div className="flex gap-8 items-center text-right">
                  <div>
                    <p className="label-eyebrow">Eventos</p>
                    <p className="font-mono text-lg mt-0.5" data-testid={`event-count-${g.restaurant.id}`}>{g.eventCount}</p>
                  </div>
                  <div>
                    <p className="label-eyebrow">Turnos totales</p>
                    <p className="font-mono text-lg mt-0.5">{g.totalSessions}</p>
                  </div>
                  <div>
                    <p className="label-eyebrow">Aforo total</p>
                    <p className="font-mono text-lg mt-0.5">{g.totalPax || "—"}</p>
                  </div>
                  <div>
                    <p className="label-eyebrow">Valor 90%</p>
                    <p className="font-mono text-lg mt-0.5">{formatEUR(g.totalV90)}</p>
                  </div>
                  <div>
                    <p className="label-eyebrow">Valor 100%</p>
                    <p className="font-mono text-lg mt-0.5">{formatEUR(g.totalPax * (g.restaurant.price_per_pax || 0))}</p>
                  </div>
                </div>
              </button>
              {!collapsed && (
                <>
                  <div className="grid grid-cols-12 px-6 py-2 border-b border-zinc-200 text-xs uppercase tracking-wider text-zinc-500 font-semibold bg-zinc-50/50">
                    <div className="col-span-2">Fecha</div>
                    <div className="col-span-1">Servicio</div>
                    <div className="col-span-1">Turnos</div>
                    <div className="col-span-2">Aforo mín · máx</div>
                    <div className="col-span-2">Valor 90% · 100% del evento</div>
                    <div className="col-span-2">Estado</div>
                    <div className="col-span-2 text-right">Acciones</div>
                  </div>
                  {g.events.map((e) => {
                    const sess = effSessions(e);
                    const pmax = (g.restaurant.pax_per_session || 0) * sess;
                    const pmin = (g.restaurant.pax_min_per_session
                      ? g.restaurant.pax_min_per_session
                      : (g.restaurant.pax_per_session || 0) * (g.restaurant.billing_base_percent || 90) / 100) * sess;
                    const price = g.restaurant.price_per_pax || 0;
                    const fmtNum = (n) => {
                      const v = Number(n || 0);
                      return Number.isInteger(v) ? v.toString() : v.toLocaleString("es-ES", { maximumFractionDigits: 2 });
                    };
                    const statusBadge = {
                      confirmed: "bg-emerald-50 text-emerald-700 border-emerald-200",
                      confirmed_late: "bg-purple-50 text-purple-700 border-purple-300",
                      provisional: "bg-amber-50 text-amber-700 border-amber-200",
                    }[e.status] || "bg-zinc-50 text-zinc-700 border-zinc-200";
                    const statusLabel = {
                      confirmed: "Confirmado",
                      confirmed_late: "Nuevo confirmado",
                      provisional: "Provisional",
                    }[e.status] || e.status;
                    return (
                      <div key={e.id} className="grid grid-cols-12 px-6 py-3 border-b border-zinc-100 items-center text-sm hover:bg-zinc-50" data-testid={`event-${e.id}`}>
                        <div className="col-span-2 font-mono">{formatDate(e.date)}</div>
                        <div className="col-span-1">{e.meal_type === "lunch" ? "Comida" : "Cena"}</div>
                        <div className="col-span-1 font-mono">x{sess}</div>
                        <div className="col-span-2 font-mono">
                          {pmax ? <span>{fmtNum(pmin)} · <span className="text-zinc-500">{fmtNum(pmax)}</span></span> : "—"}
                        </div>
                        <div className="col-span-2 font-mono text-xs">
                          <span className="text-zinc-900">{formatEUR(price * pmin)}</span>
                          <span className="text-zinc-400"> · </span>
                          <span className="text-zinc-600">{formatEUR(price * pmax)}</span>
                        </div>
                        <div className="col-span-2">
                          <span className={`text-xs px-2 py-1 border uppercase tracking-wider ${statusBadge}`} data-testid={`status-${e.id}`}>{statusLabel}</span>
                        </div>
                        <div className="col-span-2 flex justify-end gap-2">
                          {isAdmin && e.status === "provisional" && (
                            <button onClick={() => confirmEvent(e)} data-testid={`confirm-${e.id}`} className="inline-flex items-center text-xs px-2 py-1 border border-purple-400 text-purple-700 hover:bg-purple-50 uppercase tracking-wider">
                              <Check className="h-3 w-3 mr-1" strokeWidth={1.5} /> Confirmar
                            </button>
                          )}
                          {isAdmin && (
                            <>
                              <button onClick={() => startEdit(e)} data-testid={`event-edit-${e.id}`}><Edit className="h-4 w-4 text-zinc-600" strokeWidth={1.5} /></button>
                              <button onClick={() => remove(e)} data-testid={`event-delete-${e.id}`}><Trash2 className="h-4 w-4 text-zinc-600 hover:text-red-600" strokeWidth={1.5} /></button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          );
        })}
      </div>
      )}

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Subir Excel del calendario</DialogTitle>
            <DialogDescription>Se importan a la wave actual: <strong>{currentWave?.name}</strong>. No se duplican fechas ya existentes. Las celdas verdes se importan como Confirmadas, las naranjas como Provisionales.</DialogDescription>
          </DialogHeader>
          <div className="py-3">
            <input type="file" accept=".xlsx,.xls" onChange={(e) => setImportFile(e.target.files?.[0] || null)} className="text-sm" data-testid="import-file" />
            {importFile && <p className="text-sm font-mono mt-3 text-zinc-600">{importFile.name}</p>}
          </div>
          <DialogFooter>
            <Button onClick={importSubmit} disabled={importLoading || !importFile} className="rounded-none bg-zinc-900 hover:bg-zinc-800" data-testid="import-submit">
              {importLoading ? "Importando…" : "Importar (sin duplicar)"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick-create dialog */}
      <Dialog open={!!quickCreate} onOpenChange={(o) => !o && setQuickCreate(null)}>
        <DialogContent className="rounded-none max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Crear evento</DialogTitle>
            <DialogDescription>
              {quickCreate && (
                <>
                  <strong>{restMap[quickCreate.restaurantId]?.name}</strong>{" "}
                  · <span className="font-mono">{quickCreate.date}</span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs uppercase tracking-wider">Estado</Label>
              <div className="grid grid-cols-2 gap-2 mt-1.5">
                <button
                  type="button"
                  onClick={() => setQuickForm({ ...quickForm, status: "confirmed" })}
                  data-testid="qc-status-confirmed"
                  className={`p-3 border-2 text-sm font-medium transition-colors ${quickForm.status === "confirmed" ? "border-emerald-600 bg-emerald-50 text-emerald-800" : "border-zinc-200 hover:border-zinc-400"}`}
                >
                  Confirmado
                </button>
                <button
                  type="button"
                  onClick={() => setQuickForm({ ...quickForm, status: "provisional" })}
                  data-testid="qc-status-provisional"
                  className={`p-3 border-2 text-sm font-medium transition-colors ${quickForm.status === "provisional" ? "border-amber-500 bg-amber-50 text-amber-800" : "border-zinc-200 hover:border-zinc-400"}`}
                >
                  Pendiente
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider">Eventos en el día</Label>
                <div className="grid grid-cols-2 gap-2 mt-1.5">
                  {[1, 2].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setQuickForm({ ...quickForm, eventos: n })}
                      data-testid={`qc-eventos-${n}`}
                      className={`p-2 border-2 text-sm font-mono transition-colors ${quickForm.eventos === n ? "border-zinc-900 bg-zinc-50" : "border-zinc-200 hover:border-zinc-400"}`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-zinc-500 mt-1">{quickForm.eventos === 2 ? "Comida + Cena" : "Cena"}</p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Dietas del día (total)</Label>
                <div className="grid grid-cols-3 gap-2 mt-1.5">
                  {[0, 1, 2].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setQuickForm({ ...quickForm, dietas: n })}
                      data-testid={`qc-dietas-${n}`}
                      className={`p-2 border-2 text-sm font-mono transition-colors ${quickForm.dietas === n ? "border-zinc-900 bg-zinc-50" : "border-zinc-200 hover:border-zinc-400"}`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-zinc-500 mt-1">
                  {quickForm.eventos === 2
                    ? `Se repartirán entre comida y cena (${Math.floor(quickForm.dietas / 2)}+${Math.ceil(quickForm.dietas / 2)})`
                    : `Se aplicarán al evento único`}
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={submitQuickCreate} className="rounded-none bg-zinc-900 hover:bg-zinc-800" data-testid="qc-submit">
              Crear {quickForm.eventos === 2 ? "2 eventos" : "1 evento"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign employees dialog */}
      <Dialog open={!!assignDialog} onOpenChange={(o) => !o && setAssignDialog(null)}>
        <DialogContent className="rounded-none max-w-lg">
          <AssignEmployeesForm
            assignDialog={assignDialog}
            employees={employees}
            restMap={restMap}
            onSubmit={submitAssign}
            onCancel={() => setAssignDialog(null)}
          />
        </DialogContent>
      </Dialog>

      {/* Event picker (opens on any calendar cell with events) */}
      <Dialog open={!!eventPicker} onOpenChange={(o) => !o && setEventPicker(null)}>
        <DialogContent className="rounded-none max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {(eventPicker?.events || []).length > 1 ? "Eventos del día" : "Evento"}
            </DialogTitle>
            <DialogDescription>
              Acciones disponibles: asignar empleados, editar los datos, cambiar el estado o eliminar.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-2">
            {(eventPicker?.events || []).map((ev) => {
              const rest = restMap[ev.restaurant_id];
              const meal = ev.meal_type === "lunch" ? "Comida" : "Cena";
              const statusLabel = ev.status === "confirmed" ? "Confirmado" : ev.status === "confirmed_late" ? "Nuevo confirmado" : "Provisional";
              const statusCls = ev.status === "confirmed" ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                : ev.status === "confirmed_late" ? "bg-purple-100 text-purple-800 border-purple-300"
                : "bg-amber-100 text-amber-800 border-amber-300";
              const assigned = (ev.assigned_employee_ids || []).length;
              const canAssign = ev.status === "confirmed" || ev.status === "confirmed_late";
              const isConfirmed = ev.status === "confirmed" || ev.status === "confirmed_late";
              return (
                <div key={ev.id} className="border border-zinc-200 p-3 flex items-center justify-between gap-3" data-testid={`picker-event-${ev.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{meal}</span>
                      {(() => { const s = effSessions(ev); return s > 1 && <span className="font-mono text-xs text-zinc-500">×{s} turnos</span>; })()}
                      <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 border ${statusCls}`}>{statusLabel}</span>
                    </div>
                    <div className="text-xs text-zinc-500 mt-1 font-mono">
                      {rest?.name} · {ev.date} · Dietas: {ev.dietas ?? 1} · {assigned} emp.
                    </div>
                  </div>
                  <div className="flex gap-1 flex-wrap justify-end">
                    {canAssign && (
                      <button
                        onClick={() => { setEventPicker(null); setAssignDialog({ event: ev }); }}
                        className="text-xs px-2 py-1.5 border border-emerald-400 text-emerald-700 hover:bg-emerald-50 uppercase tracking-wider"
                        data-testid={`picker-assign-${ev.id}`}
                      >
                        Asignar
                      </button>
                    )}
                    {isConfirmed && (
                      <button
                        onClick={async () => { await revertToProvisional(ev); setEventPicker(null); }}
                        className="text-xs px-2 py-1.5 border border-amber-400 text-amber-800 hover:bg-amber-50 uppercase tracking-wider"
                        data-testid={`picker-toprov-${ev.id}`}
                        title="Devolver a estado Provisional"
                      >
                        → Provisional
                      </button>
                    )}
                    {ev.status === "provisional" && (
                      <button
                        onClick={async () => { await confirmEvent(ev); setEventPicker(null); }}
                        className="text-xs px-2 py-1.5 border border-purple-400 text-purple-700 hover:bg-purple-50 uppercase tracking-wider"
                        data-testid={`picker-confirm-${ev.id}`}
                      >
                        Confirmar
                      </button>
                    )}
                    <button
                      onClick={() => { setEventPicker(null); startEdit(ev); }}
                      className="text-xs px-2 py-1.5 border border-zinc-300 hover:bg-zinc-50 uppercase tracking-wider"
                      data-testid={`picker-edit-${ev.id}`}
                    >
                      Editar
                    </button>
                    <button
                      onClick={async () => {
                        if (!window.confirm("¿Eliminar evento?")) return;
                        await api.delete(`/events/${ev.id}`);
                        toast.success("Eliminado");
                        setEventPicker(null);
                        load();
                      }}
                      className="text-xs px-2 py-1.5 border border-red-300 text-red-700 hover:bg-red-50 uppercase tracking-wider"
                      data-testid={`picker-delete-${ev.id}`}
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AssignEmployeesForm({ assignDialog, employees, restMap, onSubmit, onCancel }) {
  const ev = assignDialog?.event;
  const [selected, setSelected] = useState(() => new Set(ev?.assigned_employee_ids || []));
  const assignedKey = (ev?.assigned_employee_ids || []).join(",");
  // Reset selection when opening dialog for a different event.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setSelected(new Set(ev?.assigned_employee_ids || [])); }, [ev?.id, assignedKey]);
  if (!ev) return null;
  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const rest = restMap[ev.restaurant_id];
  return (
    <>
      <DialogHeader>
        <DialogTitle className="font-heading text-2xl">Asignar empleados</DialogTitle>
        <DialogDescription>
          <strong>{rest?.name}</strong> · <span className="font-mono">{ev.date}</span>{" "}
          · {ev.meal_type === "lunch" ? "Comida" : "Cena"} · {ev.status === "confirmed_late" ? "Nuevo confirmado" : "Confirmado"}
          {" · Dietas: "}
          <span className="font-mono">{ev.dietas ?? 1}</span>
        </DialogDescription>
      </DialogHeader>
      <div className="py-2 max-h-80 overflow-y-auto border border-zinc-200">
        {employees.length === 0 && (
          <div className="p-6 text-center text-sm text-zinc-500">No hay empleados. Dalos de alta en la sección Empleados.</div>
        )}
        {employees.map((e) => {
          const isOn = selected.has(e.id);
          const rateForEvent = ((e.event_rates || {})[ev.restaurant_id] || {})[ev.meal_type] || 0;
          const showRate = ev.status === "confirmed_late";
          return (
            <button
              key={e.id}
              type="button"
              onClick={() => toggle(e.id)}
              data-testid={`assign-emp-${e.id}`}
              className={`w-full text-left px-4 py-2.5 border-b border-zinc-100 flex items-center gap-3 hover:bg-zinc-50 ${isOn ? "bg-emerald-50/50" : ""}`}
            >
              <span className={`w-4 h-4 border-2 flex-shrink-0 ${isOn ? "border-emerald-600 bg-emerald-600" : "border-zinc-300"}`}>
                {isOn && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{e.name}</div>
                <div className="text-xs text-zinc-500 font-mono">
                  {e.team && <>{e.team} · </>}
                  {showRate
                    ? <>Evento: {formatEUR(rateForEvent)} · Dieta: {formatEUR(e.rate_dieta || 0)}</>
                    : <>En bruto · Dieta: {formatEUR(e.rate_dieta || 0)}</>
                  }
                </div>
              </div>
            </button>
          );
        })}
      </div>
      <DialogFooter>
        <Button onClick={onCancel} variant="outline" className="rounded-none" data-testid="assign-cancel">Cancelar</Button>
        <Button onClick={() => onSubmit(ev.id, Array.from(selected))} className="rounded-none bg-zinc-900 hover:bg-zinc-800" data-testid="assign-save">
          Guardar ({selected.size})
        </Button>
      </DialogFooter>
    </>
  );
}

// ===== Calendar sub-view (matrix: restaurants × days) =====
const MONTH_NAMES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const WEEKDAY_LETTER = ["L","M","X","J","V","S","D"]; // ES weekday abbreviation, Mon-first

const STATUS_BG = {
  confirmed: "bg-emerald-500 text-white border-emerald-600 hover:bg-emerald-600",
  confirmed_late: "bg-purple-500 text-white border-purple-600 hover:bg-purple-600",
  provisional: "bg-amber-400 text-amber-950 border-amber-500 hover:bg-amber-500",
};

function isoDay(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Mon=0 .. Sun=6
function weekdayIdx(date) {
  return (date.getDay() + 6) % 7;
}

function CalendarView({ calMonth, setCalMonth, events, restaurants, restMap, filterCity, onEdit, onCreate, onAssign, onPickEvent, employees, waveMonthFrom, waveMonthTo }) {
  const { year, month } = calMonth;
  // Parse wave bounds (YYYY-MM)
  const parseBound = (s) => {
    if (!s || !/^\d{4}-\d{2}$/.test(s)) return null;
    const [y, m] = s.split("-").map(Number);
    return { year: y, month: m - 1 };
  };
  const bFrom = parseBound(waveMonthFrom);
  const bTo = parseBound(waveMonthTo);
  const monthIndex = year * 12 + month;
  const fromIdx = bFrom ? bFrom.year * 12 + bFrom.month : -Infinity;
  const toIdx = bTo ? bTo.year * 12 + bTo.month : Infinity;
  const canPrev = monthIndex - 1 >= fromIdx;
  const canNext = monthIndex + 1 <= toIdx;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const days = Array.from({ length: daysInMonth }, (_, i) => new Date(year, month, i + 1));
  const todayKey = isoDay(new Date());

  // Index events by restaurant_id -> day -> [events]
  const byRestDay = {};
  events.forEach((e) => {
    const dkey = (e.date || "").slice(0, 10);
    if (!dkey.startsWith(`${year}-${String(month + 1).padStart(2, "0")}`)) return;
    if (!byRestDay[e.restaurant_id]) byRestDay[e.restaurant_id] = {};
    if (!byRestDay[e.restaurant_id][dkey]) byRestDay[e.restaurant_id][dkey] = [];
    byRestDay[e.restaurant_id][dkey].push(e);
  });

  // Group restaurants by city, filtered
  const visibleRests = restaurants.filter((r) => filterCity === "all" || (r.city || "Sin ciudad") === filterCity);
  const cityGroups = {};
  visibleRests.forEach((r) => {
    const c = r.city || "Sin ciudad";
    if (!cityGroups[c]) cityGroups[c] = [];
    cityGroups[c].push(r);
  });
  const cityNames = Object.keys(cityGroups).sort();

  const prev = () => {
    if (!canPrev) return;
    const m = month - 1;
    if (m < 0) setCalMonth({ year: year - 1, month: 11 });
    else setCalMonth({ year, month: m });
  };
  const next = () => {
    if (!canNext) return;
    const m = month + 1;
    if (m > 11) setCalMonth({ year: year + 1, month: 0 });
    else setCalMonth({ year, month: m });
  };
  const today = () => {
    const d = new Date();
    setCalMonth({ year: d.getFullYear(), month: d.getMonth() });
    // If we're already in current month, also scroll to today's column
    setTimeout(() => scrollToDay(d.getDate()), 50);
  };

  // Horizontal scroll ref + helpers
  const scrollRef = useRef(null);
  const scrollByDays = (n) => {
    if (scrollRef.current) scrollRef.current.scrollBy({ left: n * cellW, behavior: "smooth" });
  };
  const scrollToDay = (dayNumber) => {
    if (!scrollRef.current) return;
    const target = Math.max(0, (dayNumber - 3) * cellW); // center-ish
    scrollRef.current.scrollTo({ left: target, behavior: "smooth" });
  };
  // Vertical wheel → horizontal scroll for easier navigation (only when shift NOT pressed)
  const handleWheel = (ev) => {
    if (!scrollRef.current) return;
    if (ev.shiftKey) return; // let default horizontal scroll happen
    if (Math.abs(ev.deltaY) <= Math.abs(ev.deltaX)) return; // only intercept vertical intent
    if (ev.deltaY === 0) return;
    ev.preventDefault();
    scrollRef.current.scrollBy({ left: ev.deltaY, behavior: "auto" });
  };

  // Download entire wave as Excel (one sheet per month)
  const [downloading, setDownloading] = useState(false);
  const downloadExcel = async () => {
    try {
      setDownloading(true);
      const res = await api.get("/events/export-calendar", {
        params: { city: filterCity || "all" },
        responseType: "blob",
      });
      // Parse filename from Content-Disposition, fallback to a generic name.
      const cd = res.headers?.["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/.exec(cd);
      const fname = match ? match[1] : "calendario_wave.xlsx";
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fname);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Calendario descargado");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al descargar");
    } finally {
      setDownloading(false);
    }
  };

  // Per-month summary — usa turnos efectivos (misma fórmula que celdas / factura / asistencia).
  const counts = { confirmed: 0, confirmed_late: 0, provisional: 0, sessions: 0 };
  Object.entries(byRestDay).forEach(([rid, daysMap]) => {
    const r = restMap[rid];
    const spdLocal = Number(r?.sessions_per_day) || 1;
    Object.values(daysMap).forEach((evs) => {
      const mealsLocal = evs.length ? new Set(evs.map((e) => e.meal_type)).size : 1;
      const effLocal = Math.max(1, Math.round(spdLocal / mealsLocal));
      evs.forEach((e) => {
        if (counts[e.status] !== undefined) counts[e.status] += 1;
        counts.sessions += Math.max(effLocal, Number(e.sessions) || 1);
      });
    });
  });

  // Cell width for day columns
  const cellW = 38;
  const nameW = 220;

  return (
    <div className="bg-white border border-zinc-200" data-testid="events-calendar">
      <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between bg-zinc-50">
        <div className="flex items-center gap-3">
          <button onClick={prev} disabled={!canPrev} data-testid="cal-prev" title="Mes anterior" className={`p-1.5 border border-zinc-300 transition-colors ${canPrev ? "hover:bg-white" : "opacity-40 cursor-not-allowed"}`}>
            <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
          </button>
          <button onClick={next} disabled={!canNext} data-testid="cal-next" title="Mes siguiente" className={`p-1.5 border border-zinc-300 transition-colors ${canNext ? "hover:bg-white" : "opacity-40 cursor-not-allowed"}`}>
            <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
          </button>
          <button onClick={today} data-testid="cal-today" className="px-3 py-1.5 border border-zinc-300 hover:bg-white text-xs uppercase tracking-wider">
            Hoy
          </button>
          <div className="w-px h-6 bg-zinc-300 mx-1" />
          <button onClick={() => scrollByDays(-7)} data-testid="cal-scroll-week-prev" title="Retroceder 7 días" className="p-1.5 border border-zinc-300 hover:bg-white transition-colors">
            <ChevronsLeft className="h-4 w-4" strokeWidth={1.5} />
          </button>
          <button onClick={() => scrollByDays(7)} data-testid="cal-scroll-week-next" title="Avanzar 7 días" className="p-1.5 border border-zinc-300 hover:bg-white transition-colors">
            <ChevronsRight className="h-4 w-4" strokeWidth={1.5} />
          </button>
          <h3 className="font-heading text-2xl ml-2">{MONTH_NAMES[month]} <span className="text-zinc-500">{year}</span></h3>
        </div>
        <div className="flex items-center gap-4 text-xs">
          <button
            onClick={downloadExcel}
            disabled={downloading}
            data-testid="cal-export-xlsx"
            title="Descargar la wave completa (todos los meses en una sola pestaña) como Excel"
            className={`flex items-center gap-1.5 px-3 py-1.5 border border-zinc-900 bg-zinc-900 text-white uppercase tracking-wider hover:bg-zinc-800 transition-colors ${downloading ? "opacity-60 cursor-wait" : ""}`}
          >
            <Download className="h-3.5 w-3.5" strokeWidth={1.5} /> {downloading ? "Generando…" : "Descargar Excel (wave)"}
          </button>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-emerald-500" /> {counts.confirmed} confirmados</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-purple-500" /> {counts.confirmed_late} nuevos</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-amber-400" /> {counts.provisional} provisionales</span>
          <span className="flex items-center gap-1.5 font-mono"><span className="text-zinc-500">turnos:</span> {counts.sessions}</span>
        </div>
      </div>

      <div className="overflow-x-auto" ref={scrollRef} onWheel={handleWheel} data-testid="cal-scroll-container">
        <div style={{ minWidth: nameW + daysInMonth * cellW + 60 }}>
          {/* Header rows: weekday + day number */}
          <div className="flex border-b border-zinc-200 bg-zinc-50 sticky top-0 z-20">
            <div
              className="px-3 py-2 border-r border-zinc-300 flex items-center text-xs uppercase tracking-wider font-semibold text-zinc-600 bg-zinc-50 sticky left-0 z-30"
              style={{ width: nameW, minWidth: nameW }}
            >
              Restaurante
            </div>
            {days.map((d) => {
              const wd = weekdayIdx(d);
              const isWeekend = wd >= 5;
              const isToday = isoDay(d) === todayKey;
              return (
                <div
                  key={d.getDate()}
                  className={`flex flex-col items-center justify-center border-r border-zinc-200 text-[10px] py-1 ${isWeekend ? "bg-zinc-100" : ""} ${isToday ? "bg-zinc-900 text-white" : "text-zinc-600"}`}
                  style={{ width: cellW, minWidth: cellW }}
                >
                  <span className="uppercase tracking-wider">{WEEKDAY_LETTER[wd]}</span>
                  <span className={`font-mono text-sm leading-tight ${isToday ? "text-white" : "text-zinc-900"}`}>{d.getDate()}</span>
                </div>
              );
            })}
            <div
              className="px-3 py-2 border-l border-zinc-300 flex items-center justify-center text-xs uppercase tracking-wider font-semibold text-zinc-600 bg-zinc-50 sticky right-0 z-30"
              style={{ width: 60, minWidth: 60 }}
            >
              Total
            </div>
          </div>

          {/* Body: city groups with restaurant rows */}
          {cityNames.map((city) => {
            const rests = cityGroups[city];
            return (
              <div key={city} data-testid={`cal-city-${city}`}>
                <div
                  className="flex border-b border-zinc-200 bg-zinc-900 text-white"
                  style={{ minWidth: nameW + daysInMonth * cellW + 60 }}
                >
                  <div className="px-3 py-1.5 label-eyebrow text-white sticky left-0 bg-zinc-900 z-10">
                    {city}
                  </div>
                </div>
                {rests.map((r) => {
                  const dayMap = byRestDay[r.id] || {};
                  const spdRest = Number(r?.sessions_per_day) || 1;
                  let rowTotal = 0;
                  return (
                    <div key={r.id} className="flex border-b border-zinc-100 hover:bg-zinc-50/50" data-testid={`cal-rest-${r.id}`}>
                      <div
                        className="px-3 py-1.5 border-r border-zinc-200 flex items-center text-sm bg-white sticky left-0 z-10"
                        style={{ width: nameW, minWidth: nameW }}
                      >
                        <span className="truncate font-medium" title={r.name}>{r.name}</span>
                      </div>
                      {days.map((d) => {
                        const dkey = isoDay(d);
                        const evs = dayMap[dkey] || [];
                        const isWeekend = weekdayIdx(d) >= 5;
                        const isToday = dkey === todayKey;
                        // Turnos EFECTIVOS por evento = restaurant.sessions_per_day / nº comidas ese día
                        // (misma fórmula que facturación/cashout/asistencia). Mantenemos MAX con el valor legacy
                        // guardado (event.sessions) para no perder data de eventos antiguos.
                        const mealsThatDay = evs.length ? new Set(evs.map((e) => e.meal_type)).size : 1;
                        const effPerEvent = Math.max(1, Math.round(spdRest / mealsThatDay));
                        const effSessions = (e) => Math.max(effPerEvent, Number(e.sessions) || 1);
                        const sessionsHere = evs.reduce((s, e) => s + effSessions(e), 0);
                        rowTotal += sessionsHere;
                        let topStatus = null;
                        if (evs.some((e) => e.status === "confirmed_late")) topStatus = "confirmed_late";
                        else if (evs.some((e) => e.status === "confirmed")) topStatus = "confirmed";
                        else if (evs.some((e) => e.status === "provisional")) topStatus = "provisional";
                        const handleCellClick = () => {
                          if (evs.length === 0) {
                            if (onCreate) onCreate(r.id, dkey);
                          } else if (onPickEvent) {
                            onPickEvent(evs);
                          } else if (evs.length === 1 && onEdit) {
                            onEdit(evs[0]);
                          }
                        };
                        const assignedCount = evs.reduce((s, e) => s + ((e.assigned_employee_ids || []).length), 0);
                        return (
                          <div
                            key={dkey}
                            className={`border-r border-zinc-100 p-0.5 flex items-stretch ${isWeekend && !topStatus ? "bg-zinc-50" : ""} ${isToday && !topStatus ? "ring-1 ring-zinc-900 ring-inset" : ""} ${evs.length === 0 && onCreate ? "group cursor-pointer hover:bg-zinc-100/50" : ""}`}
                            style={{ width: cellW, minWidth: cellW, height: 34 }}
                            data-testid={`cal-cell-${r.id}-${dkey}`}
                            onClick={evs.length === 0 ? handleCellClick : undefined}
                          >
                            {topStatus ? (
                              <button
                                onClick={handleCellClick}
                                disabled={!onEdit && !onAssign}
                                title={evs.map((e) => `${e.meal_type === "lunch" ? "Comida" : "Cena"} · ${effSessions(e)} turno(s) · ${e.status}${(e.assigned_employee_ids || []).length ? ` · ${(e.assigned_employee_ids || []).length} emp.` : ""}`).join("\n")}
                                className={`w-full h-full border text-[10px] font-mono leading-none flex flex-col items-center justify-center transition-colors ${STATUS_BG[topStatus]} ${onEdit || onAssign ? "cursor-pointer" : "cursor-default"}`}
                              >
                                <span>{sessionsHere > 1 ? `×${sessionsHere}` : "●"}</span>
                                {assignedCount > 0 && <span className="text-[8px] opacity-80">{assignedCount}p</span>}
                              </button>
                            ) : (
                              evs.length === 0 && onCreate && (
                                <span className="w-full h-full flex items-center justify-center text-zinc-300 group-hover:text-zinc-600 transition-colors text-[12px] leading-none select-none">+</span>
                              )
                            )}
                          </div>
                        );
                      })}
                      <div
                        className="px-2 py-1.5 border-l border-zinc-200 flex items-center justify-center font-mono text-sm text-zinc-700 bg-white sticky right-0 z-10"
                        style={{ width: 60, minWidth: 60 }}
                      >
                        {rowTotal || ""}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}

          {cityNames.length === 0 && (
            <div className="p-10 text-center text-sm text-zinc-500">Sin restaurantes para mostrar.</div>
          )}
        </div>
      </div>
    </div>
  );
}

